import { db, Query } from '@/firebase';
import { RUNNIT_TYPE, RUNNITS_ACCESS_LEVEL } from '@run-diffusion/shared';
import { FacetOption } from '@/utils/libraryFilterFacets';

// Board ("Board" = a Runnit of type WORKFLOW) options for the Library filter's
// board picker. Firestore rules gate `runnits` reads per access level, so the
// readable set can't come from one broad `teamId` query — it's the union of the
// same scoped reads the Boards dashboard uses. Mirrors the user-email picker:
// capped, deduped, sorted, searched client-side.
export const BOARD_LOAD_CAP = 200;

interface LoadBoardsArgs {
	userId: string;
	teamIds: string[];        // teams whose accessible boards to include
	includePersonal: boolean; // also include your personal (teamId == null) boards
}

// Run one board query into { value: id, text: title, avatarId } options. A query
// the caller can't read resolves to [] (logged) rather than rejecting the union.
// The avatar itself isn't resolved here: the picker reads `runnits` with raw
// one-time queries (not vuexfire), so `avatar` would arrive as an unresolved
// reference — instead each option carries its `avatarId`, and FilterOptionAvatar
// $binds it lazily when the row renders, so opening the panel fetches no avatars.
async function fetchBoardOptions (query: Query): Promise<FacetOption[]> {
	try {
		const snap = await query.get();
		const out: FacetOption[] = [];
		snap.forEach((doc) => {
			const d = doc.data();
			if (d) out.push({ value: doc.id, text: d.title || 'Untitled board', avatarId: d.avatarId || null });
		});
		return out;
	} catch (e) {
		console.error('Failed to load boards for the library board filter', e);
		return [];
	}
}

// Every board query the caller can read for the requested set: your personal
// boards (when includePersonal) plus, per team, your PRIVATE + member RESTRICTED
// + all SHARED boards — matching the dashboard's scoped reads. RESTRICTED skips
// the orderBy (no composite index, same as the dashboard); the union is sorted
// by title client-side below.
function boardQueries (args: LoadBoardsArgs): Query[] {
	const { userId, teamIds, includePersonal } = args;
	const queries: Query[] = [];
	if (includePersonal) {
		queries.push(
			db.collection('runnits')
				.where('userId', '==', userId)
				.where('teamId', '==', null)
				.where('deletedAt', '==', null)
				.where('type', '==', RUNNIT_TYPE.WORKFLOW)
				.orderBy('createdAt', 'desc')
				.limit(BOARD_LOAD_CAP),
		);
	}
	teamIds.filter((id) => !!id).forEach((teamId) => {
		// Your own PRIVATE boards in this team.
		queries.push(
			db.collection('runnits')
				.where('teamId', '==', teamId)
				.where('accessLevel', '==', RUNNITS_ACCESS_LEVEL.PRIVATE)
				.where('deletedAt', '==', null)
				.where('type', '==', RUNNIT_TYPE.WORKFLOW)
				.where('userId', '==', userId)
				.orderBy('createdAt', 'desc')
				.limit(BOARD_LOAD_CAP),
		);
		// RESTRICTED boards you're a member of.
		queries.push(
			db.collection('runnits')
				.where('teamId', '==', teamId)
				.where('accessLevel', '==', RUNNITS_ACCESS_LEVEL.RESTRICTED)
				.where('deletedAt', '==', null)
				.where('type', '==', RUNNIT_TYPE.WORKFLOW)
				.where(`userIds.${userId}`, '==', true)
				.limit(BOARD_LOAD_CAP),
		);
		// All of the team's SHARED boards.
		queries.push(
			db.collection('runnits')
				.where('teamId', '==', teamId)
				.where('accessLevel', '==', RUNNITS_ACCESS_LEVEL.SHARED)
				.where('deletedAt', '==', null)
				.where('type', '==', RUNNIT_TYPE.WORKFLOW)
				.orderBy('createdAt', 'desc')
				.limit(BOARD_LOAD_CAP),
		);
	});
	return queries;
}

// Load the board picker's options: run the readable queries, dedupe by board id,
// and sort by title for the client-side search. The caller maps the active scope
// to the args — Mine → every team + personal; Team → the picked teams.
export async function loadBoardsForScope (args: LoadBoardsArgs): Promise<FacetOption[]> {
	if (!args.userId) return [];
	const queries: Query[] = boardQueries(args);
	if (!queries.length) return [];
	const results: FacetOption[][] = await Promise.all(queries.map(fetchBoardOptions));
	const byId: Record<string, FacetOption> = {};
	results.forEach((opts) => opts.forEach((opt) => {
		if (!byId[opt.value]) byId[opt.value] = opt;
	}));
	return Object.values(byId).sort((a, b) => a.text.localeCompare(b.text));
}
